import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosService from '../services/axios.service';
import {
    CompetencyDTO,
    CreateCompetencyDto,
    UpdateCompetencyDto,
    CompetencyImportResult
} from '../../../shared/types';
import { useCrewModuleEnabled } from './useCrews';

// API functions
const getCompetencies = async (crewSlug: string, options?: { category?: string; isActive?: boolean }) => {
    const params = new URLSearchParams();
    if (options?.category) params.append('category', options.category);
    if (options?.isActive !== undefined) params.append('isActive', String(options.isActive));

    const { data: responseData } = await axiosService.get(
        `/c/${crewSlug}/queso/competencies${params.toString() ? '?' + params.toString() : ''}`
    );
    return responseData.data;
};

const getCompetenciesGrouped = async (crewSlug: string) => {
    const { data: responseData } = await axiosService.get(`/c/${crewSlug}/queso/competencies/grouped`);
    return responseData.data;
};

const getCompetency = async (crewSlug: string, competencyId: string) => {
    const { data: responseData } = await axiosService.get(`/c/${crewSlug}/queso/competencies/${competencyId}`);
    return responseData.data;
};

const createCompetency = async (crewSlug: string, data: CreateCompetencyDto) => {
    const { data: responseData } = await axiosService.post(`/c/${crewSlug}/queso/competencies`, data);
    return responseData.data;
};

const updateCompetency = async (crewSlug: string, competencyId: string, data: UpdateCompetencyDto) => {
    const { data: responseData } = await axiosService.put(`/c/${crewSlug}/queso/competencies/${competencyId}`, data);
    return responseData.data;
};

const deactivateCompetency = async (crewSlug: string, competencyId: string) => {
    const { data: responseData } = await axiosService.delete(`/c/${crewSlug}/queso/competencies/${competencyId}`);
    return responseData.data;
};

const exportCompetencies = async (crewSlug: string): Promise<void> => {
    const response = await axiosService.get(`/c/${crewSlug}/queso/competencies/export`, {
        responseType: 'blob'
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${crewSlug}-competencies.yaml`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

const importCompetencies = async (crewSlug: string, yamlContent: string): Promise<CompetencyImportResult> => {
    const { data: responseData } = await axiosService.post(
        `/c/${crewSlug}/queso/competencies/import`,
        { yaml: yamlContent }
    );
    return responseData.data as CompetencyImportResult;
};

const getCompetencyDetails = async (crewSlug: string, competencyId: string) => {
    const { data: responseData } = await axiosService.get(`/c/${crewSlug}/queso/competencies/${competencyId}/details`);
    return responseData.data;
};

const getCompetencyTeams = async (crewSlug: string, competencyId: string, page: number = 1, limit: number = 10) => {
    const { data: responseData } = await axiosService.get(
        `/c/${crewSlug}/queso/competencies/${competencyId}/teams`,
        { params: { page, limit } }
    );
    return {
        teams: responseData.data,
        pagination: responseData.pagination
    };
};

const getCompetencyMentors = async (crewSlug: string, competencyId: string, page: number = 1, limit: number = 10) => {
    const { data: responseData } = await axiosService.get(
        `/c/${crewSlug}/queso/competencies/${competencyId}/mentors`,
        { params: { page, limit } }
    );
    return {
        mentors: responseData.data,
        pagination: responseData.pagination
    };
};

// Custom hook
export const useCompetencies = (crewSlug?: string) => {
    const queryClient = useQueryClient();
    const competencyDefinitionsEnabled = useCrewModuleEnabled(crewSlug!, 'competencyDefinitions');
    // Queries
    const {
        data: competencies = [],
        isLoading: competenciesLoading,
        error: competenciesError,
        refetch: refetchCompetencies
    } = useQuery<CompetencyDTO[]>({
        queryKey: ['competencies', crewSlug],
        queryFn: () => getCompetencies(crewSlug!),
        enabled: !!crewSlug && competencyDefinitionsEnabled
    });

    const {
        data: competenciesGrouped = {},
        isLoading: groupedLoading,
        error: groupedError,
        refetch: refetchGrouped
    } = useQuery<Record<string, CompetencyDTO[]>>({
        queryKey: ['competencies', crewSlug, 'grouped'],
        queryFn: () => getCompetenciesGrouped(crewSlug!),
        enabled: !!crewSlug && competencyDefinitionsEnabled
    });

    // Mutations
    const createCompetencyMutation = useMutation({
        mutationFn: (data: CreateCompetencyDto) => createCompetency(crewSlug!, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['competencies', crewSlug] });
        }
    });

    const updateCompetencyMutation = useMutation({
        mutationFn: ({ competencyId, data }: { competencyId: string; data: UpdateCompetencyDto }) =>
            updateCompetency(crewSlug!, competencyId, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['competencies', crewSlug] });
        }
    });

    const deactivateCompetencyMutation = useMutation({
        mutationFn: (competencyId: string) => deactivateCompetency(crewSlug!, competencyId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['competencies', crewSlug] });
        }
    });

    const exportCompetenciesMutation = useMutation({
        mutationFn: () => exportCompetencies(crewSlug!)
    });

    const importCompetenciesMutation = useMutation({
        mutationFn: (yamlContent: string) => importCompetencies(crewSlug!, yamlContent),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['competencies', crewSlug] });
        }
    });

    const isLoading = competenciesLoading || groupedLoading;
    const error = competenciesError || groupedError;

    return {
        // Data
        competencies,
        competenciesGrouped,

        // Loading states
        isLoading,
        competenciesLoading,
        groupedLoading,

        // Errors
        error,
        competenciesError,
        groupedError,

        // Refetch
        refetchCompetencies,
        refetchGrouped,

        // Mutations
        createCompetency: createCompetencyMutation.mutate,
        updateCompetency: updateCompetencyMutation.mutate,
        deactivateCompetency: deactivateCompetencyMutation.mutate,
        exportCompetencies: exportCompetenciesMutation.mutate,
        importCompetencies: importCompetenciesMutation.mutate,

        // Mutation states
        createCompetencyMutation,
        updateCompetencyMutation,
        deactivateCompetencyMutation,
        exportCompetenciesMutation,
        importCompetenciesMutation
    };
};

// Hook for fetching competency details with parent and children
export const useCompetencyDetails = (crewSlug: string, competencyId: string) => {
    return useQuery({
        queryKey: ['competency', crewSlug, competencyId, 'details'],
        queryFn: () => getCompetencyDetails(crewSlug, competencyId),
        enabled: !!crewSlug && !!competencyId
    });
};

// Hook for fetching teams that have selected this competency
export const useCompetencyTeams = (crewSlug: string, competencyId: string, page: number = 1, limit: number = 10) => {
    return useQuery({
        queryKey: ['competency', crewSlug, competencyId, 'teams', page, limit],
        queryFn: () => getCompetencyTeams(crewSlug, competencyId, page, limit),
        enabled: !!crewSlug && !!competencyId,
        placeholderData: (previousData) => previousData
    });
};

// Hook for fetching mentors who offer this competency
export const useCompetencyMentors = (crewSlug: string, competencyId: string, page: number = 1, limit: number = 10) => {
    return useQuery({
        queryKey: ['competency', crewSlug, competencyId, 'mentors', page, limit],
        queryFn: () => getCompetencyMentors(crewSlug, competencyId, page, limit),
        enabled: !!crewSlug && !!competencyId,
        placeholderData: (previousData) => previousData
    });
};
